import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { LayoutDashboard, FlaskConical, Activity } from 'lucide-react';
import { Layout } from './components/Layout';
import { ErrorBoundary } from './components/ErrorBoundary';
import { StatusBadge } from './components/StatusBadge';

// =============================================================================
// ARKTURIAN TEMPLATE — Minimal demo App
// Replace this file when you instantiate the template.
//
// Visual idiom (1:1 from /var/code/rc-simulator/frontend, portfolio flat theme):
//   - White canvas, no card shadows, no rounded corners
//   - Roboto + Roboto Flex (Google Fonts loaded via index.css)
//   - 240px black/red-accent sidebar with lucide icons + collapse toggle
//   - Stat cards as bordered grid items with thin left accent
//   - Buttons: .btn-primary (dark) + .btn-secondary (outline)
//   - Inputs: borderless with bottom border, JetBrains Mono font
//   - Status colors: success #16A34A, warning #D97706, error #DC2626, info #2563EB
//
// To use as base for a new project:
//   1. Replace `navItems` with your routes
//   2. Replace `<HomePage>` with your views (or add multiple <Route>)
//   3. Edit package.json `name` and index.html `<title>`
// =============================================================================

const navItems = [
  { path: '/', label: 'Home', icon: LayoutDashboard, end: true },
  { path: '/examples', label: 'Examples', icon: FlaskConical },
  { path: '/about', label: 'About', icon: Activity },
];

function HomePage() {
  return (
    <div className="max-w-4xl">
      <h1 className="text-3xl font-medium tracking-tight mb-2 text-[var(--text-body)]">
        Arkturian Template
      </h1>
      <p className="text-[15px] text-[var(--text-muted)] mb-8">
        Empty starting point for new Arkturian frontends. Portfolio flat theme,
        white canvas, red accent. Replace the routes and views below with your
        own — keep the layout chrome.
      </p>

      <div className="section-title">Quick Stats</div>
      <div className="grid grid-cols-5 gap-6 mb-12">
        {[
          { label: 'Sensors', value: '12', icon: Activity, color: 'var(--accent)' },
          { label: 'Reservoirs', value: '03', icon: FlaskConical, color: 'var(--color-info)' },
          { label: 'Trainings', value: '00', icon: LayoutDashboard, color: 'var(--color-success)' },
          { label: 'Reports', value: '00', icon: FlaskConical, color: 'var(--color-warning)' },
          { label: 'Exports', value: '00', icon: LayoutDashboard, color: 'var(--text-muted)' },
        ].map((s) => (
          <div key={s.label} className="stat-card" style={{ borderLeft: `2px solid ${s.color}`, paddingLeft: 16 }}>
            <div className="flex items-center gap-2 mb-2">
              <s.icon size={14} strokeWidth={1.5} style={{ color: s.color }} />
              <span className="text-[10px] tracking-[0.18em] uppercase text-[var(--text-muted)]">{s.label}</span>
            </div>
            <div className="text-[28px] font-light text-[var(--text-body)] tabular-nums">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="section-title">Status Tokens</div>
      <div className="flex flex-wrap gap-3 mb-12">
        <StatusBadge status="completed" />
        <StatusBadge status="running" />
        <StatusBadge status="failed" />
        <StatusBadge status="pending" />
        <StatusBadge status="draft" />
      </div>

      <div className="section-title">Buttons</div>
      <div className="flex gap-3 mb-12">
        <button className="btn-primary">Primary Action</button>
        <button className="btn-primary" style={{ background: 'var(--accent)' }}>Accent</button>
        <button
          className="btn-primary"
          style={{
            background: 'transparent',
            color: 'var(--text-primary)',
            border: '1px solid var(--border)',
          }}
        >
          Secondary
        </button>
      </div>

      <div className="section-title">Form Inputs</div>
      <div className="space-y-4 max-w-md mb-12">
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-[var(--text-muted)] block mb-1">
            Label
          </label>
          <input className="input" placeholder="placeholder.mono" defaultValue="42.0" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-[var(--text-muted)] block mb-1">
            Select
          </label>
          <select>
            <option>option a</option>
            <option>option b</option>
            <option>option c</option>
          </select>
        </div>
      </div>
    </div>
  );
}

function ExamplesPage() {
  return (
    <div>
      <h1 className="text-3xl font-medium tracking-tight mb-2">Examples</h1>
      <p className="text-[15px] text-[var(--text-muted)]">
        Add example pages here, or remove this route in App.tsx.
      </p>
    </div>
  );
}

function AboutPage() {
  return (
    <div className="max-w-2xl">
      <h1 className="text-3xl font-medium tracking-tight mb-2">About</h1>
      <p className="text-[15px] text-[var(--text-muted)] mb-4">
        This template was extracted from <code className="font-mono text-[13px]">rc-simulator/frontend</code> —
        the canonical Arkturian portfolio flat theme.
      </p>
      <p className="text-[15px] text-[var(--text-muted)]">
        Live demo: <a href="https://arkturian-template.arkturian.com" className="text-[var(--accent)] hover:underline">arkturian-template.arkturian.com</a>
      </p>
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          <Route element={<Layout brand="template" brandPrefix="arkturian" navItems={navItems} footer="template · v0.1" />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/examples" element={<ExamplesPage />} />
            <Route path="/about" element={<AboutPage />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
